import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

interface VideoStepInterfaceProps {
  connectionId: string;
  currentUserId?: string;
}

export const VideoStepInterface: React.FC<VideoStepInterfaceProps> = ({ 
  connectionId,
  currentUserId = 'user123' 
}) => {
  const [videoActive, setVideoActive] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [videoDuration, setVideoDuration] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed'>('connecting');
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Video timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (videoActive) {
      interval = setInterval(() => {
        setVideoDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [videoActive]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startVideo = async () => {
    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      streamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      setVideoActive(true);
      setVideoDuration(0);
      setConnectionStatus('connected');

      // Update database
      await updateDoc(doc(db, 'connectionFlows', connectionId), {
        videoStatus: 'active',
        videoStartTime: new Date()
      });

      // Simulate remote video after 2 seconds
      setTimeout(() => {
        if (remoteVideoRef.current) {
          // In real implementation, this would be the remote stream
          remoteVideoRef.current.src = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3e%3crect width='100' height='100' fill='%23ddd'/%3e%3ctext x='50' y='50' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='14'%3eRemote User%3c/text%3e%3c/svg%3e";
        }
      }, 2000);

    } catch (error) {
      console.error('Error starting video:', error);
      setConnectionStatus('failed');
    }
  };

  const endVideo = async () => {
    setVideoActive(false);
    
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      videoStatus: 'ended',
      videoEndTime: new Date(),
      videoDuration: videoDuration
    });

    // Auto transition to physical meeting step
    setTimeout(async () => {
      await updateDoc(doc(db, 'connectionFlows', connectionId), {
        currentStep: 'physical',
        stepStartTime: new Date(),
        stepDuration: 0 // No time limit for physical meeting planning
      });
    }, 1000);
  };

  const toggleCamera = () => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !isCameraOn;
        setIsCameraOn(!isCameraOn);
      }
    }
  };

  const toggleMic = () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isMicOn;
        setIsMicOn(!isMicOn);
      }
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!videoActive) {
    return (
      <div className="h-full flex flex-col">
        <div className="bg-purple-50 p-4 rounded-lg mb-4">
          <h3 className="font-semibold text-purple-800 mb-2">Videogesprek</h3>
          <p className="text-purple-600 text-sm">
            Voer een videogesprek van 5 minuten. Zo kunnen jullie elkaar ook visueel leren kennen.
          </p>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-40 h-40 bg-purple-100 rounded-full flex items-center justify-center mb-6">
            <span className="text-5xl">📹</span>
          </div>
          
          <h4 className="text-xl font-semibold mb-2">Videogesprek Starten</h4>
          <p className="text-gray-600 mb-6 text-center max-w-md">
            Zorg ervoor dat je camera en microfoon toegang hebben. Dit gesprek duurt maximaal 5 minuten.
          </p>
          
          {connectionStatus === 'failed' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">
                Camera/microfoon toegang geweigerd. Controleer je browser instellingen.
              </p>
            </div>
          )}
          
          <button
            onClick={startVideo}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium"
          >
            Start Videogesprek
          </button>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h5 className="font-medium text-gray-800 mb-2">Voor het videogesprek:</h5>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Zorg voor goede verlichting</li>
            <li>• Test je camera en microfoon</li>
            <li>• Zoek een rustige plek</li>
            <li>• Bespreek mogelijkheden voor een fysieke ontmoeting</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isFullScreen ? 'fixed inset-0 z-50 bg-black' : 'h-full'} flex flex-col`}>
      {/* Video Header */}
      <div className={`flex justify-between items-center p-4 ${isFullScreen ? 'text-white' : 'bg-purple-50'}`}>
        <div>
          <h3 className="font-semibold">Videogesprek Actief</h3>
          <div className="text-sm opacity-75">
            {formatDuration(videoDuration)} / 5:00
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Verbonden</span>
          </div>
          
          <button
            onClick={toggleFullScreen}
            className="p-2 rounded hover:bg-black hover:bg-opacity-20"
          >
            {isFullScreen ? '🗗' : '⛶'}
          </button>
        </div>
      </div>

      {/* Video Area */}
      <div className={`flex-1 relative ${isFullScreen ? 'bg-black' : 'bg-gray-900'}`}>
        {/* Remote Video (Main) */}
        <video
          ref={remoteVideoRef}
          className="w-full h-full object-cover"
          autoPlay
          playsInline
        />
        
        {/* Local Video (Picture-in-Picture) */}
        <div className="absolute top-4 right-4 w-32 h-24 bg-gray-800 rounded-lg overflow-hidden border-2 border-white">
          <video
            ref={localVideoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
          />
          {!isCameraOn && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <span className="text-white text-sm">Camera uit</span>
            </div>
          )}
        </div>

        {/* Duration Overlay */}
        <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded">
          {formatDuration(videoDuration)}
        </div>
      </div>

      {/* Video Controls */}
      <div className={`flex justify-center items-center gap-4 p-4 ${isFullScreen ? 'bg-black bg-opacity-50' : 'bg-white'}`}>
        <button
          onClick={toggleMic}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            isMicOn 
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
              : 'bg-red-500 text-white hover:bg-red-600'
          }`}
        >
          {isMicOn ? '🎤' : '🔇'}
        </button>
        
        <button
          onClick={toggleCamera}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            isCameraOn 
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
              : 'bg-red-500 text-white hover:bg-red-600'
          }`}
        >
          {isCameraOn ? '📹' : '📷'}
        </button>
        
        <button
          onClick={endVideo}
          className="w-12 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center"
        >
          📴
        </button>
      </div>

      {/* Progress Bar */}
      <div className={`w-full h-1 ${isFullScreen ? 'bg-black bg-opacity-50' : 'bg-gray-200'}`}>
        <div 
          className="h-full bg-purple-500 transition-all"
          style={{ width: `${Math.min((videoDuration / 300) * 100, 100)}%` }}
        />
      </div>
    </div>
  );
};


